import { useCallback, useState } from 'react';
import { Map } from 'azure-maps-control';
import { Box, Stack } from '@mui/material';
import { useTranslation } from '@chr/online-shared-ui-localizations';

import { VisibilityBooking } from '@/common/apis/dtos';
import { EmptyContent } from '@/common/components/empty-content/EmptyContent';
import { IsoToDate } from '@/common/utilities/display-utilities';
import { TrackingMap } from '@/common/components/tracking-map/TrackingMap';
import { convertToMappableStops } from '@/common/components/tracking-map/mapHelpers';
import { TransportMode } from '@/common/constants/booking-types';
import { getServiceStops, getSortedStops, getTransportModeFromDesc } from '@/common/utilities/booking';

import { CarrierSummary } from './CarrierSummary';
import { BookingProgress } from './BookingProgress';
import { TransportationPlan } from './TransportationPlan';
import { BookingStatus } from './BookingStatus';

export interface TrackingProps {
  booking: VisibilityBooking;
}

export const Tracking = ({ booking }: TrackingProps) => {
  const [map, setMap] = useState<Map | null>(null);

  const onMapReady = useCallback((mapInstance: Map) => {
    setMap(mapInstance);
  }, []);

  const translate = useTranslation();

  const { canAccessShipment, hasShipmentFetchError, isLinkedToGfsShipment } = booking;

  const stops = getSortedStops(getServiceStops(booking.services), booking.shipment?.stops);
  const mappableStops = convertToMappableStops(stops);

  const service = booking.services?.[0] ?? {};
  const serviceOffering = service.serviceOffering ?? {};
  const bookingStatusId = booking.status?.id ?? 0;
  const shipmentNumber = booking.shipmentNumber;
  const transportMode = getTransportModeFromDesc(serviceOffering.modeDescription);
  const plannedOnShipmentDate = IsoToDate(booking.plannedOnShipmentDate);

  const shipment = booking.shipment ?? {};
  const billOfLadingNumber = shipment?.billOfLadingNumber;

  const airCarrierSummaries = shipment.carrierSummaries ?? [];
  const oceanCarrierSummaries = shipment.primaryCarrierSummary ? [shipment.primaryCarrierSummary] : [];
  const carrierSummaries = transportMode === TransportMode.AIR ? airCarrierSummaries : oceanCarrierSummaries;

  // Default to the service cargo readyBy Date if the shipment readyBy Date is missing
  const pickupReadyByDate = shipment.pickUpReadyByDate || service.pickupFrom?.readyByDate;

  return (
    <Stack spacing={4} padding={1} data-testid="bookings-tracking-tab">
      <BookingStatus statusId={bookingStatusId} />

      {hasShipmentFetchError &&
        <EmptyContent
          title={translate('Something went wrong')}
          bodyText={translate('We were unable to load the transportation information. Please try reloading the page.')}
          illustration={'trafficCone'}
        />
      }

      {!hasShipmentFetchError &&
        <>
          <TransportationPlan
            shipmentNumber={shipmentNumber}
            transportMode={transportMode}
            billOfLadingNumber={billOfLadingNumber}
            plannedDate={plannedOnShipmentDate}
            canAccessShipment={canAccessShipment}
            isLinkedToGfsShipment={isLinkedToGfsShipment}
          />
          <CarrierSummary carrierSummaries={carrierSummaries} transportMode={transportMode} />
        </>
      }

      <Box>
        <TrackingMap stops={mappableStops} map={map as Map} booking={booking} onMapReady={onMapReady} />
        <BookingProgress pickupReadyByDate={pickupReadyByDate} stops={stops} />
      </Box>
    </Stack>
  );
};
